import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Info, Settings, ChevronRight } from 'lucide-react';

const nodeConfigurations = {
  manual_trigger: {
    title: 'Manual Trigger',
    realName: 'Manual Trigger',
    description: 'Inicia el workflow manualmente con un clic.',
    explanation: 'El Manual Trigger es el trigger más básico de n8n. Te permite ejecutar tu workflow manualmente haciendo clic en el botón "Execute Workflow". Es ideal para desarrollo, pruebas, y workflows que necesitas ejecutar bajo demanda en lugar de automáticamente.',
    types: [
      'Manual Execution - Ejecutar manualmente con un botón',
      'Testing - Ideal para probar workflows durante desarrollo',
      'On-demand - Para workflows que ejecutas cuando lo necesitas'
    ],
    useCases: [
      'Workflows que ejecutas bajo demanda',
      'Desarrollo y prueba de workflows',
      'Tareas manuales que requieren supervisión',
      'Workflows de aprendizaje y experimentación'
    ],
    configurations: [
      { name: 'Execution Mode', description: 'Modo de ejecución manual', type: 'select' }
    ]
  },
  schedule_trigger: {
    title: 'Schedule Trigger',
    realName: 'Schedule Trigger',
    description: 'Ejecuta el workflow según un horario programado.',
    explanation: 'El Schedule Trigger permite programar la ejecución automática de tu workflow en horarios específicos. Puedes configurar intervalos simples (cada X minutos/horas/días) o usar expresiones CRON avanzadas para programación compleja (ej: todos los lunes a las 9am).',
    types: [
      'Interval - Ejecutar cada X segundos/minutos/horas/días',
      'CRON Expression - Programación avanzada (ej: 0 9 * * 1-5)',
      'Specific Times - Horarios específicos del día'
    ],
    useCases: [
      'Tareas programadas diarias/semanales/mensuales',
      'Reportes automáticos periódicos',
      'Sincronización de datos en horarios específicos',
      'Backups automáticos programados',
      'Recordatorios y notificaciones programadas'
    ],
    configurations: [
      { name: 'Trigger Interval', description: 'Intervalo de tiempo (segundos, minutos, horas, días, semanas, meses)', type: 'select' },
      { name: 'Interval Value', description: 'Valor del intervalo (ej: 5 para "cada 5 minutos")', type: 'number' },
      { name: 'CRON Expression', description: 'Expresión CRON para programación avanzada (ej: 0 9 * * 1)', type: 'text' },
      { name: 'Start Time', description: 'Hora de inicio de la ejecución', type: 'time' }
    ]
  },
  end: {
    title: 'End Node',
    realName: 'End / Stop',
    description: 'Marca el final del flujo de trabajo. Puedes tener múltiples ends en workflows con ramificaciones.',
    explanation: 'El nodo End en n8n finaliza la ejecución del workflow. Es importante saber que puedes tener varios nodos End en el mismo workflow si tienes ramificaciones con IF o Switch. Cada rama puede terminar en su propio End. También puedes definir el estado final (success/error) y los datos que retorna el workflow.',
    types: [
      'End Normal - Finalización exitosa',
      'Stop on Error - Finalizar indicando error',
      'Multiple Ends - Varios puntos de salida según ramas'
    ],
    useCases: [
      'Finalizar workflow exitosamente después de completar tareas',
      'Terminar ramas individuales en workflows con IF/Switch',
      'Retornar datos procesados al finalizar',
      'Indicar errores o estados específicos de salida'
    ],
    configurations: [
      { name: 'Status', description: 'Estado final del workflow (success/error)', type: 'select' },
      { name: 'Output Data', description: 'Datos a retornar al finalizar', type: 'json' },
      { name: 'Message', description: 'Mensaje descriptivo de finalización', type: 'text' }
    ]
  },
  http_request: {
    title: 'HTTP Request',
    realName: 'HTTP Request',
    description: 'Realiza solicitudes HTTP a APIs externas. La columna vertebral de las integraciones en n8n.',
    explanation: 'El nodo HTTP Request es uno de los más poderosos de n8n. Te permite comunicarte con cualquier API o servicio web externo. Soporta todos los métodos HTTP (GET, POST, PUT, DELETE, PATCH), autenticación (Basic, OAuth2, API Key), headers personalizados, y manejo avanzado de respuestas. Es esencial para integrar servicios externos en tus workflows.',
    types: [
      'GET - Obtener datos de una API',
      'POST - Enviar datos nuevos',
      'PUT - Actualizar datos existentes',
      'DELETE - Eliminar recursos',
      'PATCH - Actualización parcial'
    ],
    useCases: [
      'Consultar datos de APIs REST (clima, cotizaciones, etc.)',
      'Enviar notificaciones a servicios externos (Slack, Discord)',
      'Integrar con CRMs (crear contactos, actualizar deals)',
      'Conectar con bases de datos a través de APIs',
      'Automatizar acciones en servicios SaaS'
    ],
    configurations: [
      { name: 'URL', description: 'Dirección de la API a llamar (ej: https://api.ejemplo.com/users)', type: 'text' },
      { name: 'Method', description: 'Método HTTP (GET, POST, PUT, DELETE, PATCH)', type: 'select' },
      { name: 'Authentication', description: 'Tipo de autenticación (None, Basic Auth, OAuth2, API Key)', type: 'select' },
      { name: 'Headers', description: 'Encabezados HTTP personalizados (Content-Type, Authorization, etc.)', type: 'json' },
      { name: 'Body', description: 'Datos a enviar en el body (para POST/PUT/PATCH)', type: 'json' },
      { name: 'Query Parameters', description: 'Parámetros de URL (ej: ?page=1&limit=10)', type: 'json' }
    ]
  },
  edit_fields: {
    title: 'Edit Fields (Set)',
    realName: 'Edit Fields / Set',
    description: 'Establece, modifica o crea campos de datos en tu flujo.',
    explanation: 'En n8n, este nodo se llama "Edit Fields" y te permite manipular los datos que fluyen por tu workflow. Puedes agregar nuevos campos, modificar existentes, eliminar campos no deseados, o renombrar propiedades. Es fundamental para preparar datos antes de enviarlos a otros nodos o APIs que requieren formatos específicos.',
    types: [
      'Set Values - Establecer valores manualmente',
      'Keep Only Set - Mantener solo campos específicos',
      'Remove Fields - Eliminar campos innecesarios',
      'Rename Fields - Cambiar nombres de propiedades'
    ],
    useCases: [
      'Preparar datos antes de enviarlos a una API',
      'Agregar timestamp o IDs únicos a registros',
      'Transformar nombres de campos para compatibilidad',
      'Limpiar datos eliminando campos sensibles',
      'Combinar o formatear datos de múltiples fuentes'
    ],
    configurations: [
      { name: 'Fields to Set', description: 'Campos a establecer o modificar (nombre: valor)', type: 'fields' },
      { name: 'Mode', description: 'Modo de operación (Set, Keep, Remove, Rename)', type: 'select' },
      { name: 'Value Type', description: 'Tipo de valor (Fixed, Expression, JSON)', type: 'select' },
      { name: 'Include Binary Data', description: 'Mantener datos binarios adjuntos', type: 'boolean' }
    ]
  },
  if: {
    title: 'IF Node',
    realName: 'If',
    description: 'Crea ramificaciones basadas en condiciones lógicas (true/false).',
    explanation: 'El nodo IF en n8n evalúa condiciones y divide el flujo en dos caminos: TRUE (si la condición se cumple) y FALSE (si no se cumple). Puedes comparar valores, verificar existencia de campos, evaluar rangos numéricos, y más. Es fundamental para crear workflows inteligentes que toman decisiones automáticas basadas en los datos.',
    types: [
      'Simple Condition - Una condición básica (ej: edad > 18)',
      'Multiple Conditions - Varias condiciones con AND/OR',
      'Expression - Expresiones JavaScript complejas',
      'Field Exists - Verificar si un campo existe',
      'Type Check - Verificar tipo de dato'
    ],
    useCases: [
      'Filtrar datos según criterios (ej: solo usuarios activos)',
      'Routing según prioridad (alta vs normal)',
      'Validar datos antes de procesarlos',
      'Ejecutar acciones diferentes según status (pendiente/completado)',
      'Implementar lógica de negocio condicional'
    ],
    configurations: [
      { name: 'Conditions', description: 'Condiciones a evaluar (campo, operador, valor)', type: 'conditions' },
      { name: 'Combine', description: 'Combinar múltiples condiciones (AND/OR)', type: 'select' },
      { name: 'Field', description: 'Campo a comparar del item', type: 'text' },
      { name: 'Operator', description: 'Operador (igual, diferente, mayor, menor, contiene, existe)', type: 'select' },
      { name: 'Value', description: 'Valor de comparación', type: 'text' }
    ]
  },
  code: {
    title: 'Code Node',
    realName: 'Code',
    description: 'Ejecuta código JavaScript personalizado para transformaciones y lógica compleja.',
    explanation: 'El nodo Code en n8n te da poder total sobre tus datos. Puedes escribir JavaScript para realizar cualquier transformación, cálculo o lógica que los nodos estándar no cubran. Tienes acceso a los items ($input), puedes hacer loops, usar librerías JavaScript modernas, manipular fechas, hacer cálculos matemáticos complejos, y retornar arrays de items personalizados.',
    types: [
      'Run Once for All Items - Ejecutar código una vez con todos los items',
      'Run Once for Each Item - Ejecutar para cada item individualmente',
      'Custom Output - Retornar estructura personalizada'
    ],
    useCases: [
      'Transformaciones complejas que requieren lógica personalizada',
      'Cálculos matemáticos avanzados (estadísticas, promedios, etc.)',
      'Manipulación de fechas (parsing, formateo, cálculos)',
      'Procesamiento de strings (regex, limpieza, validación)',
      'Agregaciones y análisis de datos',
      'Generar IDs únicos o tokens'
    ],
    configurations: [
      { name: 'Mode', description: 'Modo de ejecución (Run Once, Run Once per Item)', type: 'select' },
      { name: 'JavaScript Code', description: 'Código JavaScript a ejecutar (acceso a $input, items, etc.)', type: 'code' },
      { name: 'Libraries', description: 'Puedes usar: luxon (fechas), crypto, axios, lodash', type: 'info' },
      { name: 'Return', description: 'Retorna items usando: return items; o return [{json: {...}}]', type: 'info' }
    ]
  },
  webhook: {
    title: 'Webhook Node',
    realName: 'Webhook',
    description: 'Recibe requests HTTP de servicios externos para activar workflows en tiempo real.',
    explanation: 'El nodo Webhook en n8n genera una URL única que otros servicios pueden llamar para activar tu workflow. Es ideal para integraciones en tiempo real donde servicios externos notifican eventos (nuevo pago, formulario enviado, cambio en base de datos). n8n recibirá los datos enviados y los procesará inmediatamente.',
    types: [
      'Production Webhook - URL permanente para producción',
      'Test Webhook - URL temporal para desarrollo',
      'GET Request - Recibir datos por GET',
      'POST Request - Recibir datos por POST',
      'With Authentication - Webhook protegido con auth'
    ],
    useCases: [
      'Recibir notificaciones de Stripe (pagos, suscripciones)',
      'Procesar formularios web enviados',
      'Integraciones con GitHub (commits, pull requests)',
      'Webhooks de servicios SaaS (Shopify, WooCommerce)',
      'Recibir alertas de sistemas de monitoreo'
    ],
    configurations: [
      { name: 'Webhook URL', description: 'URL única generada automáticamente por n8n', type: 'text' },
      { name: 'HTTP Method', description: 'Método HTTP aceptado (GET, POST, PUT, DELETE)', type: 'select' },
      { name: 'Path', description: 'Ruta personalizada del webhook', type: 'text' },
      { name: 'Authentication', description: 'Proteger webhook con autenticación', type: 'select' },
      { name: 'Response', description: 'Respuesta a retornar al servicio que llamó', type: 'json' },
      { name: 'Response Code', description: 'Código HTTP de respuesta (200, 201, etc.)', type: 'number' }
    ]
  },
  merge: {
    title: 'Merge Node',
    realName: 'Merge',
    description: 'Combina datos de múltiples ramas del workflow en un solo flujo.',
    explanation: 'El nodo Merge en n8n une datos que vienen de diferentes ramas de tu workflow (por ejemplo, después de un IF o Split). Puedes elegir diferentes estrategias: Append (agregar items uno tras otro), Merge by Key (combinar items que tienen el mismo identificador), o Wait (esperar a que todas las ramas terminen antes de combinar).',
    types: [
      'Append - Agregar todos los items en orden',
      'Merge by Key - Combinar items con mismo ID/campo',
      'Merge by Position - Combinar items por posición',
      'Wait - Esperar a que todas las ramas completen'
    ],
    useCases: [
      'Unir resultados de múltiples APIs consultadas en paralelo',
      'Combinar ramas TRUE y FALSE después de un IF',
      'Juntar datos procesados de diferentes maneras',
      'Enriquecer datos combinando información de varias fuentes',
      'Consolidar resultados de procesos paralelos'
    ],
    configurations: [
      { name: 'Mode', description: 'Estrategia de merge (Append, Merge by Key, Merge by Position)', type: 'select' },
      { name: 'Join', description: 'Tipo de join (Inner, Left, Outer)', type: 'select' },
      { name: 'Property', description: 'Campo clave para merge by key', type: 'text' },
      { name: 'Wait for All', description: 'Esperar a que todas las ramas lleguen', type: 'boolean' }
    ]
  },
  split_out: {
    title: 'Split Out Node',
    realName: 'Split Out',
    description: 'Convierte arrays en items individuales para procesamiento uno por uno.',
    explanation: 'El nodo Split Out (antes llamado Item Lists) en n8n toma un campo que contiene un array y lo convierte en items individuales. Por ejemplo, si recibes [{productos: [A, B, C]}], Split Out creará 3 items separados: [A], [B], [C]. Esto es fundamental cuando una API retorna arrays y necesitas procesar cada elemento individualmente.',
    types: [
      'Split Array Field - Dividir un campo tipo array',
      'Split JSON Array - Dividir arrays JSON complejos',
      'Nested Arrays - Manejar arrays anidados'
    ],
    useCases: [
      'Procesar cada producto de un pedido individualmente',
      'Iterar sobre resultados de una API que retorna arrays',
      'Enviar email a cada usuario de una lista',
      'Procesar cada línea de un archivo CSV',
      'Crear tareas individuales desde una lista'
    ],
    configurations: [
      { name: 'Field to Split', description: 'Campo que contiene el array a dividir', type: 'text' },
      { name: 'Include Other Fields', description: 'Mantener otros campos del item original', type: 'boolean' },
      { name: 'Destination Field', description: 'Dónde colocar cada item del array', type: 'text' }
    ]
  },
  aggregate: {
    title: 'Aggregate Node',
    realName: 'Aggregate',
    description: 'Combina múltiples items en uno solo.',
    explanation: 'El nodo Aggregate en n8n es lo opuesto al Split Out. Toma múltiples items y los combina en un solo item con un array. Por ejemplo, si tienes 3 items [A], [B], [C], Aggregate los convertirá en [{items: [A, B, C]}]. Es esencial después de procesar items individuales cuando necesitas consolidar los resultados.',
    types: [
      'Combine All - Combinar todos los items en un array',
      'Aggregate by Field - Agrupar por campo específico',
      'Custom Aggregation - Agregación personalizada'
    ],
    useCases: [
      'Consolidar resultados después de Split Out',
      'Combinar respuestas de múltiples API calls',
      'Agrupar datos por categoría o tipo',
      'Preparar datos para exportación masiva',
      'Crear resúmenes de datos procesados'
    ],
    configurations: [
      { name: 'Aggregate', description: 'Cómo combinar los items (All, By Field)', type: 'select' },
      { name: 'Field Name', description: 'Nombre del campo donde guardar el array agregado', type: 'text' },
      { name: 'Include Original Fields', description: 'Mantener campos originales de cada item', type: 'boolean' }
    ]
  },
  sort: {
    title: 'Sort Node',
    realName: 'Sort',
    description: 'Ordena items según criterios especificados.',
    explanation: 'El nodo Sort en n8n ordena los items que fluyen por tu workflow según uno o más campos. Puedes ordenar de forma ascendente (A-Z, 1-9) o descendente (Z-A, 9-1). Soporta ordenamiento por múltiples campos con prioridades. Es útil para organizar datos antes de procesarlos o presentarlos.',
    types: [
      'Simple Sort - Ordenar por un campo',
      'Multi-field Sort - Ordenar por múltiples campos con prioridad',
      'Ascending/Descending - Orden ascendente o descendente',
      'Numeric/String Sort - Ordenamiento numérico o alfabético'
    ],
    useCases: [
      'Ordenar productos por precio antes de mostrarlos',
      'Organizar tareas por prioridad o fecha',
      'Clasificar usuarios por fecha de registro',
      'Ordenar resultados de búsqueda',
      'Priorizar procesamiento de items importantes'
    ],
    configurations: [
      { name: 'Sort Field', description: 'Campo por el cual ordenar', type: 'text' },
      { name: 'Sort Order', description: 'Orden de clasificación (Ascending, Descending)', type: 'select' },
      { name: 'Sort Type', description: 'Tipo de ordenamiento (String, Number, Date)', type: 'select' },
      { name: 'Additional Sorts', description: 'Campos adicionales para ordenamiento secundario', type: 'fields' }
    ]
  },
  limit: {
    title: 'Limit Node',
    realName: 'Limit',
    description: 'Limita el número de items que pasan al siguiente nodo.',
    explanation: 'El nodo Limit en n8n restringe la cantidad de items que continúan en el workflow. Puedes tomar los primeros N items, saltar los primeros X items, o combinar ambos. Es útil para controlar el volumen de datos procesados, respetar límites de APIs, o trabajar con muestras de datos.',
    types: [
      'Max Items - Limitar a N items máximo',
      'Skip Items - Saltar los primeros X items',
      'Pagination - Combinar skip y limit para paginación',
      'Sample Data - Tomar muestra para pruebas'
    ],
    useCases: [
      'Procesar solo los primeros 10 resultados',
      'Implementar paginación (skip: 20, limit: 10)',
      'Limitar requests a APIs con rate limits',
      'Tomar muestras de datos para testing',
      'Reducir volumen de datos en workflows de desarrollo'
    ],
    configurations: [
      { name: 'Max Items', description: 'Número máximo de items a pasar', type: 'number' },
      { name: 'Keep Items From', description: 'Desde dónde tomar items (Start, End)', type: 'select' }
    ]
  },
  function: {
    title: 'Function Node',
    realName: 'Function / Edit Fields',
    description: 'Aplica transformaciones predefinidas de datos sin necesidad de escribir código.',
    explanation: 'El nodo Function (también relacionado con Edit Fields) en n8n ofrece transformaciones comunes de datos de forma visual. Puedes formatear fechas, convertir entre tipos de datos (string a número), hacer cálculos matemáticos, extraer partes de textos, y más. Es ideal cuando necesitas transformar datos pero no quieres escribir código JavaScript.',
    types: [
      'Date Formatting - Formatear y parsear fechas',
      'Type Conversion - Convertir entre tipos (string, number, boolean)',
      'Math Operations - Cálculos matemáticos (+, -, *, /, %)',
      'String Operations - Manipular textos (trim, uppercase, extract)',
      'Object Operations - Manipular objetos JSON'
    ],
    useCases: [
      'Convertir timestamps a fechas legibles',
      'Extraer dominio de un email (user@domain.com → domain.com)',
      'Calcular totales o promedios',
      'Formatear números como moneda',
      'Limpiar y normalizar datos (trim, lowercase)'
    ],
    configurations: [
      { name: 'Function Type', description: 'Tipo de transformación a aplicar', type: 'select' },
      { name: 'Input Field', description: 'Campo de entrada a transformar', type: 'text' },
      { name: 'Output Field', description: 'Campo donde guardar el resultado', type: 'text' },
      { name: 'Parameters', description: 'Parámetros de la función (formato, operación, etc.)', type: 'json' },
      { name: 'Options', description: 'Opciones adicionales según la función', type: 'json' }
    ]
  },
  filter: {
    title: 'Filter Node',
    realName: 'Filter',
    description: 'Filtra items de datos según criterios específicos, eliminando los que no cumplen condiciones.',
    explanation: 'El nodo Filter en n8n te permite filtrar datos que fluyen por tu workflow. Solo los items que cumplan las condiciones que definas pasarán al siguiente nodo. Es esencial para garantizar calidad de datos antes de procesarlos, eliminar registros inválidos, o seleccionar solo los datos relevantes para tu caso de uso.',
    types: [
      'Simple Filter - Una condición básica',
      'Multiple Conditions - Varias condiciones con AND/OR',
      'Custom Expression - Expresiones JavaScript personalizadas',
      'Date Filters - Filtrar por rangos de fechas',
      'Null/Empty Filters - Eliminar valores vacíos o nulos'
    ],
    useCases: [
      'Eliminar registros con campos vacíos o nulos',
      'Filtrar por fecha (ej: solo últimos 7 días)',
      'Seleccionar solo registros activos o pendientes',
      'Remover duplicados antes de procesar',
      'Validar datos antes de enviar a API (solo emails válidos)',
      'Filtrar por rangos numéricos (ej: precios entre 10-100)'
    ],
    configurations: [
      { name: 'Conditions', description: 'Condiciones de filtrado (campo, operador, valor)', type: 'conditions' },
      { name: 'Combine', description: 'Cómo combinar múltiples condiciones (AND/OR)', type: 'select' },
      { name: 'Keep/Remove', description: 'Mantener items que cumplen o que NO cumplen', type: 'select' }
    ]
  },
  loop: {
    title: 'Loop Over Items',
    realName: 'Loop Over Items (Split in Batches)',
    description: 'Procesa datos en lotes iterativos, ideal para grandes volúmenes sin sobrecargar memoria.',
    explanation: 'El nodo Loop Over Items (también conocido como Split in Batches) en n8n procesa grandes conjuntos de datos dividiéndolos en lotes manejables. Es fundamental cuando trabajas con miles de registros y necesitas procesarlos de forma controlada sin sobrecargar memoria o respetar rate limits de APIs (ej: procesar 10 items a la vez en lugar de 10,000 simultáneamente).',
    types: [
      'Fixed Batch Size - Lotes de tamaño fijo (ej: 100 items)',
      'Dynamic Batching - Tamaño de lote adaptativo',
      'Rate Limiting - Con pausa entre lotes',
      'Parallel Processing - Procesar lotes en paralelo'
    ],
    useCases: [
      'Procesar miles de registros de base de datos en lotes',
      'Respetar rate limits de APIs (ej: máximo 10 requests por segundo)',
      'Enviar emails en lotes para no sobrecargar servidor',
      'Importar datos en bloques manejables',
      'Procesar archivos grandes línea por línea'
    ],
    configurations: [
      { name: 'Batch Size', description: 'Cantidad de items por lote (ej: 100)', type: 'number' },
      { name: 'Options', description: 'Opciones de reset del loop', type: 'json' }
    ]
  },
  switch: {
    title: 'Switch Node',
    realName: 'Switch',
    description: 'Enruta datos a múltiples caminos según condiciones evaluadas (3+ ramas posibles).',
    explanation: 'El nodo Switch en n8n es como un IF avanzado que puede tener 3, 4, 5 o más salidas diferentes. Evalúa condiciones o valores y dirige cada item por el camino correspondiente. A diferencia del IF que tiene solo 2 salidas (true/false), Switch permite routing complejo basado en múltiples criterios. Ideal para lógica de negocio con varios escenarios posibles.',
    types: [
      'Value-based Routing - Según valor de un campo',
      'Expression Routing - Según expresiones evaluadas',
      'Multiple Conditions - Múltiples reglas independientes',
      'Priority-based - Primera regla que coincide',
      'Fallback Route - Ruta por defecto si ninguna coincide'
    ],
    useCases: [
      'Enrutar por prioridad de tickets (baja/media/alta/urgente)',
      'Categorizar datos (ventas/soporte/marketing/admin)',
      'Routing por estado (pendiente/en proceso/completado/cancelado/fallido)',
      'Direccionar por región geográfica (LATAM/EMEA/NA/APAC)',
      'Separar por tipo de producto o servicio',
      'Clasificar leads por puntuación (cold/warm/hot)'
    ],
    configurations: [
      { name: 'Mode', description: 'Modo de evaluación (Rules, Expression)', type: 'select' },
      { name: 'Rules', description: 'Reglas para cada rama de salida (output 0, 1, 2, etc.)', type: 'rules' },
      { name: 'Fallback Output', description: 'Rama por defecto si no cumple ninguna regla', type: 'select' }
    ]
  },
  wait: {
    title: 'Wait Node',
    realName: 'Wait',
    description: 'Pausa la ejecución del workflow por tiempo específico o hasta que se cumpla una condición.',
    explanation: 'El nodo Wait en n8n permite pausar la ejecución de tu workflow. Puedes pausar por un tiempo fijo (segundos, minutos, horas, días), hasta una fecha/hora específica, o hasta que se cumpla una condición externa. Es fundamental para respetar rate limits de APIs, coordinar procesos asincrónicos, implementar reintentos con backoff exponencial, o esperar a que sistemas externos completen tareas.',
    types: [
      'Fixed Duration - Pausar por tiempo fijo (5 segundos, 2 minutos, etc.)',
      'Until DateTime - Pausar hasta fecha/hora específica',
      'Webhook Resume - Esperar a recibir webhook externo',
      'Form Submission - Esperar a que usuario complete formulario',
      'Condition Met - Esperar hasta que se cumpla condición'
    ],
    useCases: [
      'Respetar rate limits de APIs (ej: pausar 6 segundos entre requests)',
      'Implementar reintentos con backoff exponencial',
      'Esperar a que procesos externos completen (ej: procesamiento de pago)',
      'Coordinar workflows dependientes',
      'Crear workflows de aprobación (esperar respuesta de usuario)',
      'Pausar hasta horario laboral antes de enviar notificaciones'
    ],
    configurations: [
      { name: 'Resume', description: 'Cómo reanudar (After Time Period, At Date/Time, On Webhook, etc.)', type: 'select' },
      { name: 'Amount', description: 'Cantidad de tiempo (si es duration)', type: 'number' },
      { name: 'Unit', description: 'Unidad de tiempo (seconds, minutes, hours, days)', type: 'select' },
      { name: 'Limit', description: 'Timeout máximo de espera', type: 'duration' }
    ]
  },
  error: {
    title: 'Stop and Error',
    realName: 'Stop and Error',
    description: 'Detiene la ejecución del workflow de forma controlada indicando un error.',
    explanation: 'El nodo Stop and Error en n8n permite terminar la ejecución del workflow explícitamente con un estado de error. Es útil para validaciones críticas: si los datos no cumplen requisitos esenciales, puedes detener el flujo inmediatamente con un mensaje de error descriptivo en lugar de continuar y potencialmente causar problemas mayores. Se combina con el nodo Error Trigger para crear workflows de manejo de errores robustos.',
    types: [
      'Validation Error - Error por validación fallida',
      'Business Logic Error - Error de lógica de negocio',
      'Critical Failure - Fallo crítico que requiere atención',
      'With Custom Message - Error con mensaje personalizado'
    ],
    useCases: [
      'Validar datos críticos antes de procesarlos (ej: email requerido)',
      'Detener workflow si API retorna error crítico',
      'Implementar validaciones de negocio estrictas',
      'Prevenir procesamiento de datos inválidos o corruptos',
      'Crear puntos de fallo controlados para debugging',
      'Activar workflows de error handling con Error Trigger'
    ],
    configurations: [
      { name: 'Error Message', description: 'Mensaje descriptivo del error ocurrido', type: 'text' },
      { name: 'Error Details', description: 'Detalles adicionales del error (JSON)', type: 'json' }
    ]
  },
  execute_workflow: {
    title: 'Execute Workflow',
    realName: 'Execute Workflow',
    description: 'Ejecuta otro workflow como componente reutilizable dentro del workflow actual.',
    explanation: 'El nodo Execute Workflow en n8n permite llamar a otros workflows como si fueran funciones reutilizables. Esto es fundamental para arquitecturas modulares: puedes crear workflows especializados (ej: "validar-email", "enviar-notificacion", "calcular-impuestos") y reutilizarlos en múltiples workflows principales. Promueve DRY (Don\'t Repeat Yourself), facilita mantenimiento y testing, y permite equipos trabajar en workflows independientes.',
    types: [
      'Call & Wait - Ejecutar y esperar resultado',
      'Fire & Forget - Ejecutar sin esperar',
      'With Input Data - Pasar datos al sub-workflow',
      'Nested Workflows - Workflows que llaman a otros workflows'
    ],
    useCases: [
      'Reutilizar lógica de validación en múltiples workflows',
      'Modularizar workflows complejos en componentes manejables',
      'Crear librerías de workflows comunes (enviar email, logging, etc.)',
      'Separar concerns (autenticación, logging, transformación)',
      'Implementar microservicios con n8n',
      'Testing más fácil de componentes individuales'
    ],
    configurations: [
      { name: 'Source', description: 'Workflow a ejecutar (Database, Parameter)', type: 'select' },
      { name: 'Workflow ID', description: 'ID del workflow a ejecutar', type: 'text' },
      { name: 'Mode', description: 'Modo de ejecución (Synchronous, Asynchronous)', type: 'select' },
      { name: 'Input Data', description: 'Datos a pasar al sub-workflow', type: 'json' }
    ]
  },
  ai_agent: {
    title: 'AI Agent (Tools Agent)',
    realName: 'AI Agent',
    description: 'Agente inteligente que usa un LLM para razonar, tomar decisiones y ejecutar herramientas automáticamente.',
    explanation: 'El nodo AI Agent en n8n es un nodo cluster que combina un modelo de lenguaje (LLM) como OpenAI, Anthropic o Google Gemini con herramientas que puede usar para resolver tareas complejas. El agente recibe un prompt con una tarea, razona qué herramientas necesita, las ejecuta secuencialmente o en loops, y devuelve el resultado final. Es la forma más avanzada de automatización inteligente en n8n: el agente toma decisiones por sí mismo sobre qué hacer y cómo hacerlo.',
    types: [
      'Tools Agent - Agente con acceso a herramientas (recomendado)',
      'Conversational Agent - Para diálogos y contexto conversacional',
      'OpenAI Functions Agent - Usa function calling de OpenAI',
      'Plan and Execute Agent - Planifica pasos antes de ejecutar',
      'ReAct Agent - Reasoning and Action pattern',
      'SQL Agent - Especializado en queries de bases de datos'
    ],
    useCases: [
      'Asistente inteligente que busca info (Wikipedia, Google) y responde',
      'Automatizar análisis de datos con cálculos y razonamiento',
      'Clasificar y enrutar tickets de soporte automáticamente',
      'Generar reportes combinando datos de múltiples fuentes',
      'Responder preguntas sobre documentos (RAG con vector stores)',
      'Ejecutar acciones complejas según contexto (enviar emails, actualizar DBs)'
    ],
    configurations: [
      { name: 'Language Model', description: 'Modelo de IA a usar (OpenAI, Anthropic, Gemini, etc.)', type: 'connection' },
      { name: 'Chat Memory', description: 'Sistema de memoria para mantener contexto conversacional', type: 'connection' },
      { name: 'Tools', description: 'Herramientas que el agente puede usar (Calculator, Wikipedia, Code, APIs, etc.)', type: 'connections' },
      { name: 'Prompt', description: 'Instrucciones para el agente sobre qué tarea realizar', type: 'textarea' },
      { name: 'System Message', description: 'Instrucciones permanentes sobre comportamiento del agente', type: 'textarea' },
      { name: 'Max Iterations', description: 'Máximo número de veces que puede llamar herramientas (evita loops infinitos)', type: 'number' },
      { name: 'Return Intermediate Steps', description: 'Incluir pasos de razonamiento en la respuesta', type: 'boolean' }
    ]
  }
};

const NodeConfigSidebar = ({ nodeType, isOpen, onClose }) => {
  const config = nodeConfigurations[nodeType] || {};

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 z-[100]"
            onClick={onClose}
          />
          
          {/* Sidebar */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#1A1D3D] border-l border-primary/30 shadow-2xl z-[150] flex flex-col"
            data-testid="node-config-sidebar"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary/20 to-secondary/20 p-5 border-b border-white/10 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/30 flex items-center justify-center border-2 border-primary/50">
                    <Info className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-heading font-bold text-text-primary">{config.title}</h2>
                    <p className="text-text-secondary text-xs">Configuración y uso del nodo</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-10 h-10 rounded-full bg-background-subtle hover:bg-error/20 text-text-muted hover:text-error transition-all flex items-center justify-center"
                  data-testid="close-sidebar-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Description */}
              <div className="bg-background-paper/50 p-4 rounded-xl border border-white/5">
                <h3 className="text-base font-heading font-bold text-text-primary mb-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                  ¿Qué hace?
                </h3>
                <p className="text-text-primary text-sm leading-relaxed mb-2">{config.description}</p>
                <p className="text-text-secondary text-xs leading-relaxed">{config.explanation}</p>
              </div>

              {/* Real Name in n8n */}
              {config.realName && (
                <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 p-4 rounded-xl border border-blue-500/20">
                  <h3 className="text-sm font-heading font-bold text-text-primary mb-2 flex items-center gap-2">
                    <span className="text-blue-400">🏷️</span>
                    En n8n se llama:
                  </h3>
                  <p className="text-blue-200 text-sm font-mono bg-blue-900/30 px-3 py-1.5 rounded-lg inline-block">
                    {config.realName}
                  </p>
                </div>
              )}

              {/* Types/Variants */}
              {config.types && config.types.length > 0 && (
                <div className="bg-background-paper/50 p-4 rounded-xl border border-white/5">
                  <h3 className="text-base font-heading font-bold text-text-primary mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary"></span>
                    Tipos disponibles
                  </h3>
                  <ul className="space-y-2">
                    {config.types.map((type, index) => (
                      <li key={index} className="flex items-start gap-2 text-text-secondary text-xs">
                        <span className="text-secondary mt-0.5">▸</span>
                        <span className="leading-relaxed">{type}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Use Cases */}
              {config.useCases && config.useCases.length > 0 && (
                <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 p-4 rounded-xl border border-green-500/20">
                  <h3 className="text-base font-heading font-bold text-text-primary mb-3 flex items-center gap-2">
                    <span className="text-green-400">💡</span>
                    Cuándo usarlo
                  </h3>
                  <ul className="space-y-2">
                    {config.useCases.map((useCase, index) => (
                      <li key={index} className="flex items-start gap-2 text-text-secondary text-xs">
                        <span className="text-green-400 mt-0.5">✓</span>
                        <span className="leading-relaxed">{useCase}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Configurations */}
              {config.configurations && config.configurations.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Settings className="w-4 h-4 text-primary" />
                    <h3 className="text-base font-heading font-bold text-text-primary">Configuraciones Disponibles</h3>
                  </div>
                  <div className="space-y-2">
                    {config.configurations.map((conf, index) => (
                      <div key={index} className="bg-background-paper/50 p-3 rounded-lg border border-white/5 hover:border-primary/20 transition-colors">
                        <div className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded-md bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-primary text-[10px] font-bold">{index + 1}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-text-primary font-bold text-sm mb-1">{conf.name}</h4>
                            <p className="text-text-secondary text-xs leading-relaxed mb-1.5">{conf.description}</p>
                            <span className="inline-block px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-mono border border-primary/20">
                              tipo: {conf.type}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tips Section */}
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-4 rounded-xl border border-primary/20">
                <div className="flex items-center gap-2 mb-2">
                  <ChevronRight className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-heading font-bold text-text-primary">Tip</h3>
                </div>
                <p className="text-text-secondary text-xs leading-relaxed">
                  Arrastra este nodo al canvas y conéctalo con otros nodos para construir tu flujo de trabajo.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 bg-background-subtle/50 border-t border-white/10 flex-shrink-0">
              <button
                onClick={onClose}
                className="w-full btn-neon py-2.5 rounded-lg font-heading font-bold text-sm"
              >
                ✓ Entendido
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NodeConfigSidebar;
